import moment from 'moment'
import { pendingSelector } from 'api/selectors'
import { createSelector } from 'reselect'
import { userSelector } from 'auth/selectors'
import { v4 } from 'uuid'

import {
    INVOICE_PAYMENT_PROGRESS,
    ASSIGN_PARKER_PROGRESS,
    REMOVE_PARKER_PROGRESS,
    SAVE_PAD_DATA_PROGRESS,
} from './constants'
import { vehiclesEntityToArray } from 'dashboard-subscriptions/utils'

const stateSelector = state => state.dashboardSubscriptions

export const subscriptionsHistorySelector = createSelector(stateSelector, state => state.subscriptions)

export const subscriptionsHistoryPendingSelector = createSelector(
    pendingSelector,
    pending => pending['subscriptionHistory']
)

export const getWaitListSelectorPendingSelector = createSelector(pendingSelector, pending => pending['waitList'])

export const subscriptionInvoicesLoadingSelector = createSelector(
    pendingSelector,
    pending => pending['subscriptionInvoices']
)

export const subscriptionReceiptsLoadingSelector = createSelector(
    pendingSelector,
    pending => pending['subscriptionReceipts']
)

export const subscriptionInvoicesSelector = createSelector(stateSelector, state => state.subscriptionInvoices)

export const subscriptionReceiptsSelector = createSelector(stateSelector, state => state.subscriptionReceipts)

export const updateSubscriptionPendingSelector = createSelector(
    pendingSelector,
    pending => pending['updateSubscription']
)

export const paymentMethodsSelector = createSelector(userSelector, ({ creditCards = [], padMethods = [] }) =>
    [...creditCards, ...padMethods].map(c => ({
        ...c,
        value: c.id,
    }))
)

export const waitListSelector = createSelector(stateSelector, state => state.waitList)

export const subscriptionSelector = id =>
    createSelector(subscriptionsHistorySelector, subscriptions => {
        const subscription = subscriptions.find(sub => id === sub.id)
        const { members } = subscription

        /*
            If products object has > 1 product Then check each product which is currently active
            Loop through each product until you find a first active product
            If productEndDate is not null Then check if productStartDate <= currentDate<= productEndDate  then currently active that hasnt ended yet.
            Else if productStartDate  <= currentDate then currently active
            If none of the products match Then take the first product from array as it is in the future
            Else the only product is active (Currently active)
            Check if currently active by looping through all products until we get a match.
         */

        const updatedMembers = members.filter(member => {
            if (member.deletedDate || member.products.length < 1) {
                return false
            }

            member.products = member.products.filter(product => {
                if (product.productEndDate) {
                    return (
                        moment(product.productStartDate).isBefore(moment().format('MM-DD-YYYY')) &&
                        moment(product.productEndDate).isAfter(moment().format('MM-DD-YYYY'))
                    )
                } else if (product.productStartDate) {
                    return moment(product.productStartDate).isBefore(moment().format('MM-DD-YYYY'))
                }

                return true
            })

            return member.products.length > 0
        })

        return { ...subscription, members: updatedMembers }
    })

export const productsSelector = id =>
    createSelector(subscriptionSelector(id), subscription => subscription?.members || [])

export const paymentMethodSelector = id =>
    createSelector(subscriptionSelector(id), subscription =>
        subscription.paymentMethods ? subscription.paymentMethods[0] : {}
    )

export const invoicePaymentPendingSelector = createSelector(
    pendingSelector,
    pending => pending[INVOICE_PAYMENT_PROGRESS]
)

export const parkerDataForAssignSelector = data =>
    createSelector(subscriptionSelector(data.subscriptionId), subscription => {
        const { members } = subscription

        const member = members.findIndex(m => m.id === data.memberId)

        members[member].firstName = data.user?.firstName
        members[member].lastName = data.user?.lastName

        if (data.selfService) {
            members[member].emailAddress = data.user?.emailAddress
        }

        const fieldList = data?.selectedVehicles?.map(vehicle => {
            let fieldMap
            if (vehicle.isDeleted) {
                fieldMap = {
                    color: {
                        inactive: false,
                        value: vehicle.color,
                    },
                    model: {
                        inactive: false,
                        value: vehicle.model,
                    },

                    make: {
                        inactive: false,
                        value: vehicle.make,
                    },
                    country: {
                        code: 'country',
                        inactive: false,
                        value: vehicle.country,
                    },

                    state: {
                        value: vehicle.state,
                    },
                    plateNumber: {
                        inactive: false,
                        value: vehicle.tag,
                    },
                    vehicleId: {
                        inactive: false,
                        value: vehicle.id || v4(),
                        code: 'vehicleId',
                    },
                    plateNumberSearch: {
                        value: vehicle.tag,
                    },
                    isDeleted: {
                        value: true,
                    },
                }
            } else
                fieldMap = {
                    plateNumber: {
                        inactive: false,
                        value: vehicle.tag,
                    },
                    vehicleId: {
                        inactive: false,
                        value: vehicle.id || v4(),
                        code: 'vehicleId',
                    },
                    plateNumberSearch: {
                        value: vehicle.tag,
                    },
                }

            return { code: 'vehicle', inactive: false, fieldMap }
        })

        members[member].extended = {
            inactive: false,
            fieldMap: {
                vehicles: {
                    inactive: false,
                    fieldList,
                },
            },
        }

        return members
    })

export const assignParkerPendingSelector = createSelector(pendingSelector, pending => pending[ASSIGN_PARKER_PROGRESS])
export const removeParkerPendingSelector = createSelector(pendingSelector, pending => pending[REMOVE_PARKER_PROGRESS])
export const savePadPendingSelector = createSelector(pendingSelector, pending => pending[SAVE_PAD_DATA_PROGRESS])

export const assignedParkerSelector = (subscriptionId, memberId) =>
    createSelector(subscriptionSelector(subscriptionId), subscription => {
        const { members } = subscription

        if (members && members[0]) {
            const member = members.find(m => m.id === memberId)

            if (member) {
                const { firstName, lastName, emailAddress, extended } = member
                const vehicles = vehiclesEntityToArray(member)
                const selfServiceConfirmed = extended?.fieldMap?.selfService?.value || false

                const userData = {
                    firstName,
                    lastName,
                    emailAddress,
                    vehicles,
                    selfServiceConfirmed,
                    memberId,
                }

                return userData
            }
        }

        return {}
    })
